// How a side looks for its move in Godsmode. A knight gives a pitch at the
// start: one careful, one aggro. The player sees each pitch as a move on the
// board and may take it or not. The AI plays by its pitches:
//   both     It weighs the two against each other, which is the full search.
//   one      It has one view of the board. It plays that pitch, at the depth
//            of a pitch, in the style of the pitch.
//   none     It sees one move ahead and no reply.
// Rob, 2026-10-13: "Our chess engine should actually understand the interplay
// between careful and aggro suggestions, and perform worse when it loses one,
// the other, or especially both."

import { AI_BLIND_DEPTH, AI_HINT_DEPTH, AI_OWN_AGGRO_DEPTH, AI_TIME_BUDGET_MS } from '../constants';
import { livingPitches } from '../engine/roles';
import type { Move, Pitch, Side } from '../engine/types';
import type { GameState } from '../game/state';
import type { SearchRequest, SearchResult } from './search';

/** How far the search of a side looks. `full` is as far as its clock allows. */
export type SearchStrength = 'full' | 'pitch' | 'blind';

export type SearchPlan = { strength: SearchStrength; style: Pitch };

/** The plan of the side to move. A match with a wheel has no pitches, and its AI is at full strength. */
export const searchPlanFor = (state: GameState): SearchPlan => {
  if (state.mode !== 'gods') return { strength: 'full', style: 'careful' };
  // suggest-best gives the side a full look for the turn, with or without its knights.
  if (state.suggestFor === state.turn) return { strength: 'full', style: 'careful' };
  const pitches = livingPitches(state.board, state.turn);
  if (pitches.has('careful') && pitches.has('aggro')) return { strength: 'full', style: 'careful' };
  if (pitches.has('careful')) return { strength: 'pitch', style: 'careful' };
  if (pitches.has('aggro')) return { strength: 'pitch', style: 'aggro' };
  return { strength: 'blind', style: 'careful' };
};

/** The depth limit of a plan in the game. `full` has none but the clock. */
export const planDepth = (plan: SearchPlan): number | undefined =>
  plan.strength === 'full' ? undefined : plan.strength === 'pitch' ? AI_HINT_DEPTH : AI_BLIND_DEPTH;

/** What the AI lacks, for the beat that shows it thinks. Plain words, not final copy. Empty at full strength. */
export const planNote = (plan: SearchPlan): string =>
  plan.strength === 'full'
    ? ''
    : plan.strength === 'pitch'
      ? `It has only the ${plan.style} pitch.`
      : 'It has no pitch.';

export const isSameMove = (a: Move | null | undefined, b: Move | null | undefined): boolean =>
  !!a && !!b && a.from === b.from && a.to === b.to;

/**
 * The searches for the tie break of the pitches. When the careful pitch and
 * the aggro pitch are the same move, the piece that gives the aggro pitch
 * offers a move of its own: the one its side scores best in the aggro style,
 * with no look at the reply. The move both pitches gave is left out, so the
 * two pitches differ when that piece has another move. One request for each
 * piece of the side that gives the aggro pitch: a copy gives it too. None
 * when another piece must move.
 */
export const ownAggroRequests = (state: GameState, side: Side, taken: Move): SearchRequest[] =>
  state.board.flatMap((piece) => {
    if (piece?.owner !== side || piece.pitch !== 'aggro') return [];
    if (state.mustMovePieceId !== null && state.mustMovePieceId !== piece.id) return [];
    return [
      {
        board: state.board,
        rules: state.rules,
        side,
        onlyPieceId: piece.id,
        budgetMs: AI_TIME_BUDGET_MS,
        maxDepth: AI_OWN_AGGRO_DEPTH,
        style: 'aggro' as const,
        skipMove: taken,
      },
    ];
  });

/** The best move among the results of those searches. Null when no piece that gives the aggro pitch has another move. */
export const bestOwnAggro = (results: readonly SearchResult[]): Move | null => {
  let best: SearchResult | null = null;
  for (const result of results) {
    if (result.move && (!best || result.score > best.score)) best = result;
  }
  return best?.move ?? null;
};
